import React, { useState } from 'react'
import {
  Box,
  Typography,
  Collapse,
  Button,
  Paper,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SpeedIcon from '@mui/icons-material/Speed'

interface TtsMetricsPanelProps {
  ttfbMs: number
  totalTimeMs: number
  charactersProcessed: number
  estimatedCost?: number
  requestId?: string
  audioDurationS?: number
}

interface MetricCardProps {
  label: string
  value: string
  sub?: string
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, sub }) => (
  <Paper
    elevation={0}
    sx={{
      px: 2,
      py: 1.5,
      borderRadius: 2,
      backgroundColor: 'rgba(0, 74, 87, 0.08)',
      border: '1px solid rgba(0, 74, 87, 0.15)',
      minWidth: 90,
      flex: '1 1 auto',
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: '#004a57', mt: 0.25 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
        {sub}
      </Typography>
    )}
  </Paper>
)

const formatMs = (ms: number): string => {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.round(ms)} ms`
}

const TtsMetricsPanel: React.FC<TtsMetricsPanelProps> = ({
  ttfbMs,
  totalTimeMs,
  charactersProcessed,
  estimatedCost,
  requestId,
  audioDurationS,
}) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Box>
      <Button
        size="small"
        startIcon={<SpeedIcon sx={{ fontSize: 16 }} />}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          fontSize: '0.8rem',
          px: 1,
          '&:hover': { backgroundColor: 'rgba(0, 74, 87, 0.06)' },
        }}
      >
        Performance Metrics
      </Button>

      <Collapse in={expanded}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1,
          }}
        >
          <MetricCard label="TTFB" value={formatMs(ttfbMs)} sub="first byte" />
          <MetricCard label="Total Time" value={formatMs(totalTimeMs)} />
          <MetricCard
            label="Characters"
            value={charactersProcessed.toLocaleString()}
            sub="processed"
          />
          {audioDurationS !== undefined && (
            <MetricCard label="Audio" value={`${audioDurationS.toFixed(1)}s`} sub="duration" />
          )}
          {estimatedCost !== undefined && (
            <MetricCard label="Cost" value={`~€${estimatedCost.toFixed(3)}`} sub="estimated" />
          )}
          {requestId && (
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 74, 87, 0.08)',
                border: '1px solid rgba(0, 74, 87, 0.15)',
                flex: '2 1 auto',
                minWidth: 160,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Request ID
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  mt: 0.25,
                  wordBreak: 'break-all',
                }}
              >
                {requestId}
              </Typography>
            </Paper>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default TtsMetricsPanel
